/*
 * request-quote.js
 *
 * Two modes of operation, auto-detected by URL path:
 *
 * 1. Product page — intercepts clicks on `[data-request-quote]` links,
 *    collects the live LPO/variant state (color, quantity, decoration method,
 *    placement zones) and redirects to /pages/quote?product=...&color=... with
 *    all the context as URL params.
 *
 * 2. Quote page (/pages/quote) — reads the URL params, renders a summary
 *    block in the quote section, and prefills the contact form's message
 *    textarea with a pretty-formatted breakdown so the visitor only needs to
 *    add their name/email and send.
 */
(function () {
  'use strict';

  // ─────────────────────────────────────────────────────────────────────────
  //  MODE 1: Product page — capture state on click
  // ─────────────────────────────────────────────────────────────────────────
  function collectProductState(trigger) {
    const state = {
      product: trigger.dataset.productTitle || '',
      handle: trigger.dataset.productHandle || '',
    };

    // Volume tier hint — from data-tier-hint (e.g. "100+", "250+")
    if (trigger.dataset.tierHint) state.tier = trigger.dataset.tierHint;

    // Variant ID — from form or URL
    const form = document.querySelector('form[action*="/cart/add"]');
    const idInput = form && form.querySelector('[name="id"]');
    if (idInput && idInput.value) state.variant = idInput.value;
    else {
      const urlVariant = new URLSearchParams(location.search).get('variant');
      if (urlVariant) state.variant = urlVariant;
    }

    // Color — try multiple selectors
    const colorRadio = document.querySelector('input[name*="Color" i]:checked, input[name*="Garment color" i]:checked');
    const swatchInput = document.querySelector('.swatch-input.is-checked input, .swatch-input input:checked');
    const colorSelect = document.querySelector('select[name*="Color" i] option:checked, select[name*="Garment color" i] option:checked');
    const activeSwatchLabel = document.querySelector('[name*="options[Color" i]:checked + label, [name*="options[Garment color" i]:checked + label');
    state.color =
      (colorRadio && colorRadio.value) ||
      (swatchInput && swatchInput.value) ||
      (colorSelect && colorSelect.textContent.trim()) ||
      (activeSwatchLabel && activeSwatchLabel.textContent.trim()) ||
      '';

    // Quantity — prefer per-size breakdown
    const SIZE_PATTERN = /^(XS|S|M|L|XL|2XL|3XL|4XL|5XL|6XL|OSFA|OSFM)$/i;
    const breakdown = [];
    let total = 0;

    // (a) ct-customizer snippet inputs (#ct-qty-S, #ct-qty-M, ...)
    document.querySelectorAll('.ct-qty-input[id^="ct-qty-"]').forEach(function (inp) {
      const qty = parseInt(inp.value || '0', 10) || 0;
      if (qty > 0) {
        const size = inp.id.replace('ct-qty-', '').toUpperCase();
        breakdown.push(size + ':' + qty);
        total += qty;
      }
    });

    // (b) LPO size options: .cl-po--option with a size label + .cl-po--quantity input
    if (!breakdown.length) {
      document.querySelectorAll('.cl-po--option').forEach(function (opt) {
        const labelEl = opt.querySelector('.cl-po--label');
        const qtyInp = opt.querySelector('.cl-po--quantity');
        if (!labelEl || !qtyInp) return;
        const label = labelEl.textContent.trim().replace(/\s+\+\s*\$.*$/, ''); // strip trailing price like "2XL +$4.00"
        if (!SIZE_PATTERN.test(label)) return;
        const qty = parseInt(qtyInp.value || '0', 10) || 0;
        if (qty > 0) {
          breakdown.push(label.toUpperCase() + ':' + qty);
          total += qty;
        }
      });
    }

    if (breakdown.length) {
      state.sizes = breakdown.join(', ');
      state.quantity = String(total);
    } else {
      const ctQty = document.getElementById('ct-quantity-hidden');
      const nativeQty = document.querySelector('quantity-input .quantity__input, input[name="quantity"]');
      state.quantity =
        (ctQty && ctQty.value) ||
        (nativeQty && nativeQty.value) ||
        '1';
    }

    // Decoration method — active button or hidden input
    const activeMethodBtn = document.querySelector('.ct-deco__method-btn.is-active');
    const methodHidden = document.querySelector('[id^="ct-prop-method-"], [name="properties[Decoration Method]" i], [name="properties[Decoration method]" i]');
    const ctDecoSelect = document.getElementById('ct-decoration-select');
    state.method =
      (activeMethodBtn && (activeMethodBtn.dataset.method || activeMethodBtn.textContent.trim())) ||
      (methodHidden && methodHidden.value) ||
      (ctDecoSelect && ctDecoSelect.value) ||
      '';

    // Placement zones — selected options or hidden comma-separated input
    const placementsHidden = document.querySelector('[id^="ct-prop-placements-"]');
    if (placementsHidden && placementsHidden.value) {
      state.placements = placementsHidden.value;
    } else {
      const selectedZones = Array.from(document.querySelectorAll('.ct-deco__option.is-selected'))
        .map(function (el) {
          const label = el.querySelector('.ct-deco__option-label, .ct-deco__option-name');
          return (label ? label.textContent : el.textContent).trim();
        })
        .filter(Boolean);
      if (selectedZones.length) state.placements = selectedZones.join(', ');
    }

    // Uploaded file — filename only (stash happens in click handler, awaitable)
    const fileInput = document.querySelector('#ct-file, input[type="file"][name*="Uploaded" i]');
    if (fileInput && fileInput.files && fileInput.files[0]) {
      state.file = fileInput.files[0].name;
    } else {
      const filenameEl = document.querySelector('[data-ct-filename]');
      if (filenameEl && filenameEl.textContent.trim()) state.file = filenameEl.textContent.trim();
    }

    // Comments / special requests
    const commentsEl = document.querySelector('#ct-comments, textarea[name*="Comments" i]');
    if (commentsEl && commentsEl.value.trim()) state.comments = commentsEl.value.trim();

    // COG (Customer's Own Goods) form fields — decoration service products
    const cogItem = document.querySelector('#ct-cog-item');
    if (cogItem && cogItem.value.trim()) state.cog_item = cogItem.value.trim();
    const cogPlacement = document.querySelector('#ct-cog-placement');
    if (cogPlacement && cogPlacement.value.trim()) state.cog_placement = cogPlacement.value.trim();

    // Remove empty values to keep URL clean
    Object.keys(state).forEach(function (k) {
      if (!state[k]) delete state[k];
    });
    return state;
  }

  // Stash uploaded files from Upload-Lift (Cloudlift) hidden inputs to sessionStorage.
  // Upload-Lift stores each uploaded file as hidden input: properties[Design file_N] = URL.
  // We extract all such inputs and stash the list of {name, url} entries.
  function stashUploadedFile() {
    try { sessionStorage.removeItem('quoteFiles'); } catch (e) {}
    try { sessionStorage.removeItem('quoteFile'); } catch (e) {}

    const files = [];

    // Priority 1: Upload-Lift hidden inputs with URL value
    document.querySelectorAll('input[type="hidden"]').forEach(function (inp) {
      if (!inp.value || !/^https?:\/\//.test(inp.value)) return;
      if (!/^properties\[/i.test(inp.name || '')) return;
      // Only file-looking URLs (has extension or from cloudlift)
      if (!/\.(png|jpe?g|gif|webp|pdf|ai|svg|eps)(\?|$)/i.test(inp.value) && !/cloudlift|upload/i.test(inp.value)) return;

      // Extract filename from URL
      let fname = inp.value.split('/').pop().split('?')[0] || 'file';
      // Try to find display filename from Cloudlift FilePond UI
      const filePondFile = document.querySelector('.filepond--file-info-main');
      if (filePondFile && filePondFile.textContent.trim() && files.length === 0) {
        fname = filePondFile.textContent.trim();
      }
      files.push({ name: fname, url: inp.value, fromProduct: true });
    });

    if (files.length) {
      try {
        sessionStorage.setItem('quoteFiles', JSON.stringify(files));
      } catch (e) {
        console.warn('[request-quote] sessionStorage stash failed:', e);
      }
    }
    return Promise.resolve();
  }

  function debugUploadLift() {
    console.log('%c=== Upload-Lift Debug ===', 'background:#ffeb3b;color:#000;font-weight:bold;padding:2px 6px');

    console.log('%c[1] Hidden inputs with URL/file value:', 'color:#0a7; font-weight:bold');
    let found1 = 0;
    document.querySelectorAll('input[type="hidden"]').forEach(function (i) {
      if (i.value && (/^https?:\/\//.test(i.value) || /\.(png|jpe?g|pdf|ai|svg|eps|webp|gif)/i.test(i.value))) {
        console.log('  📎', i.name || '(no name)', '=', i.value);
        found1++;
      }
    });
    if (!found1) console.log('  (none)');

    console.log('%c[2] All file inputs:', 'color:#0a7; font-weight:bold');
    document.querySelectorAll('input[type="file"]').forEach(function (i) {
      console.log('  id=' + (i.id || '-') + ' name=' + (i.name || '-') + ' class=' + (i.className || '-') + ' | has file:', !!(i.files && i.files[0]));
    });

    console.log('%c[3] Window globals matching upload/lift:', 'color:#0a7; font-weight:bold');
    console.log(' ', Object.keys(window).filter(function (k) { return /upload|lift/i.test(k); }));

    console.log('%c[4] Storage (localStorage + sessionStorage):', 'color:#0a7; font-weight:bold');
    ['localStorage', 'sessionStorage'].forEach(function (s) {
      for (let i = 0; i < window[s].length; i++) {
        const k = window[s].key(i);
        if (/upload|lift|file/i.test(k)) {
          const v = window[s].getItem(k);
          console.log(' ', s + '.' + k, '=', v ? v.slice(0, 300) : v);
        }
      }
    });

    console.log('%c[5] Data-* attributes with URL:', 'color:#0a7; font-weight:bold');
    let found5 = 0;
    document.querySelectorAll('*').forEach(function (el) {
      Object.keys(el.dataset || {}).forEach(function (k) {
        const v = el.dataset[k];
        if (v && /^https?:\/\//.test(v)) {
          console.log(' ', el.tagName + '[data-' + k + ']', '=', v.slice(0, 200));
          found5++;
        }
      });
    });
    if (!found5) console.log('  (none)');

    console.log('%c[6] Elements with "upload" or "lift" in id/class:', 'color:#0a7; font-weight:bold');
    const els = document.querySelectorAll('[class*="upload"], [class*="lift"], [id*="upload"], [id*="lift"]');
    console.log('  count:', els.length);
    els.forEach(function (el, idx) {
      if (idx < 20) {
        console.log(' ', el.tagName + (el.id ? '#' + el.id : '') + (el.className ? '.' + String(el.className).split(' ').join('.') : ''));
      }
    });

    console.log('%c=== End Debug ===', 'background:#ffeb3b;color:#000;font-weight:bold;padding:2px 6px');
  }

  function initProductPage() {
    const triggers = document.querySelectorAll('[data-request-quote]');
    if (!triggers.length) return;
    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        const state = collectProductState(trigger);
        const qs = new URLSearchParams(state).toString();
        stashUploadedFile().then(function () {
          window.location.href = '/pages/quote' + (qs ? '?' + qs : '');
        });
      });
    });
  }

  // Convert base64 data URL back to File (for re-attaching on quote page)
  function dataUrlToFile(dataUrl, filename, mime) {
    try {
      const byteString = atob(dataUrl.split(',')[1]);
      const buf = new ArrayBuffer(byteString.length);
      const arr = new Uint8Array(buf);
      for (let i = 0; i < byteString.length; i++) arr[i] = byteString.charCodeAt(i);
      return new File([buf], filename, { type: mime || 'application/octet-stream' });
    } catch (e) { return null; }
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  MODE 2: Quote page — prefill form from URL params
  // ─────────────────────────────────────────────────────────────────────────
  function formatSummary(params) {
    const lines = [];
    if (params.product) lines.push('Product:      ' + params.product);
    if (params.tier) lines.push('Volume tier:  ' + params.tier);
    if (params.color) lines.push('Color:        ' + params.color);
    if (params.method) lines.push('Decoration:   ' + params.method);
    if (params.placements) lines.push('Placement:    ' + params.placements);
    if (params.sizes) {
      lines.push('Sizes & Qty:  ' + params.sizes + (params.quantity ? '  (total: ' + params.quantity + ')' : ''));
    } else if (params.quantity) {
      lines.push('Quantity:     ' + params.quantity);
    }
    // COG fields — customer-supplied item details
    if (params.cog_item) lines.push('Item:         ' + params.cog_item);
    if (params.cog_placement) lines.push('Placement:    ' + params.cog_placement);
    if (params.file) lines.push('File:         ' + params.file + '  (please re-attach in reply email)');
    if (params.comments) lines.push('Comments:     ' + params.comments);
    if (params.handle) lines.push('Product page: https://crafttoryshop.com/products/' + params.handle);
    return lines.join('\n');
  }

  function formatMessageBody(params) {
    const summary = formatSummary(params);
    if (!summary) return '';
    return (
      '\n--- Quote request details ---\n' +
      summary +
      '\n\nPlease add any questions or special requirements below:\n\n'
    );
  }

  // ─── Quote page file manager ──────────────────────────────────────────────
  const MAX_FILES = 5;
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB (Cloudinary free tier accepts up to 10 MB for raw files)
  const ALLOWED_EXT = /\.(png|jpg|jpeg|gif|webp|pdf|ai|svg|eps)$/i;

  // Uploadcare unsigned upload config (public key only — safe in frontend)
  const UPLOADCARE_PUB_KEY = '5fabc9dcddf13a2dac27';
  const UPLOADCARE_ENDPOINT = 'https://upload.uploadcare.com/base/';
  const UPLOADCARE_CDN = 'https://20msfj7s8q.ucarecd.net/';

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function uploadToCloudinary(file, onProgress) {
    // Name kept for call-site compatibility; provider is Uploadcare.
    return new Promise(function (resolve, reject) {
      const formData = new FormData();
      formData.append('UPLOADCARE_PUB_KEY', UPLOADCARE_PUB_KEY);
      formData.append('UPLOADCARE_STORE', 'auto');
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', function (e) {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round(e.loaded / e.total * 100));
        }
      });
      xhr.addEventListener('load', function () {
        console.log('[uploadcare] response status:', xhr.status);
        console.log('[uploadcare] response body:', xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (!data.file) { reject(new Error('No file UUID in response')); return; }
            const safeName = encodeURIComponent(file.name);
            resolve(UPLOADCARE_CDN + data.file + '/' + safeName);
          } catch (e) { reject(new Error('Invalid response')); }
        } else {
          let errMsg = 'Upload failed (' + xhr.status + ')';
          try {
            const errData = JSON.parse(xhr.responseText);
            if (errData && errData.error) {
              errMsg = errData.error.content || errData.error.message || errMsg;
            }
          } catch (e) {}
          reject(new Error(errMsg));
        }
      });
      xhr.addEventListener('error', function () { reject(new Error('Network error')); });
      xhr.addEventListener('abort', function () { reject(new Error('Aborted')); });
      xhr.open('POST', UPLOADCARE_ENDPOINT);
      xhr.send(formData);

      // Return xhr reference for cancellation (expose via promise.abort — not standard but useful)
      return xhr;
    });
  }

  function initFileManager() {
    const root     = document.querySelector('[data-quote-files]');
    if (!root) return;
    const listEl   = root.querySelector('[data-quote-files-list]');
    const addBtn   = root.querySelector('.quote-form__files-add');
    const inputEl  = root.querySelector('[data-quote-file-input]');
    const messageEl = document.querySelector('#ContactForm-body');
    if (!listEl || !inputEl) return;

    // State: array of { name, size, type, data (base64), fromProduct }
    const state = { files: [] };

    // Inject stashed product-page files (Upload-Lift URLs)
    try {
      const stashed = sessionStorage.getItem('quoteFiles');
      if (stashed) {
        const arr = JSON.parse(stashed);
        if (Array.isArray(arr)) {
          arr.forEach(function (f) {
            if (f && f.name) {
              state.files.push({
                name: f.name,
                size: f.size || 0,
                type: f.type || '',
                url: f.url || '',
                fromProduct: true,
              });
            }
          });
        }
        sessionStorage.removeItem('quoteFiles');
      }
    } catch (e) {}

    function renderList() {
      if (!state.files.length) {
        listEl.innerHTML = '';
      } else {
        listEl.innerHTML = state.files.map(function (f, i) {
          const safeName = String(f.name).replace(/</g, '&lt;');
          const nameHtml = f.url
            ? '<a class="quote-form__file-name" href="' + f.url + '" target="_blank" rel="noopener">' + safeName + '</a>'
            : '<span class="quote-form__file-name">' + safeName + '</span>';
          const progressHtml = f.uploading
            ? '<div class="quote-form__file-progress"><div class="quote-form__file-progress-bar" style="width:' + (f.progress || 0) + '%"></div></div>'
            : '';
          const statusHtml = f.uploading
            ? '<span class="quote-form__file-status">Uploading… ' + (f.progress || 0) + '%</span>'
            : (f.error
              ? '<span class="quote-form__file-status is-error">Failed</span>'
              : (f.fromProduct ? '<span class="quote-form__file-source">from product page</span>' : ''));

          return (
            '<li>' +
              '<div class="quote-form__file-row">' +
                '<svg class="quote-form__file-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
                  '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>' +
                  '<polyline points="14 2 14 8 20 8"></polyline>' +
                '</svg>' +
                nameHtml +
                (f.size ? '<span class="quote-form__file-size">' + formatFileSize(f.size) + '</span>' : '') +
                statusHtml +
                '<button type="button" class="quote-form__file-remove" data-remove-index="' + i + '" aria-label="Remove file">' +
                  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
                    '<line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>' +
                  '</svg>' +
                '</button>' +
              '</div>' +
              progressHtml +
            '</li>'
          );
        }).join('');
      }
      // Disable "Add file" button when at limit
      if (state.files.length >= MAX_FILES) {
        addBtn.classList.add('is-disabled');
      } else {
        addBtn.classList.remove('is-disabled');
      }
      updateMessageFiles();
    }

    function updateMessageFiles() {
      if (!messageEl) return;
      // Strip any existing "Files:" / "File URL:" lines
      let body = messageEl.value || '';
      body = body.replace(/\nFiles?:\s+[^\n]*(\n|$)/g, '\n');
      body = body.replace(/\nFile URLs?:\s*\n(  [^\n]*\n)+/g, '\n');

      // Only include files that finished uploading (have URL)
      const uploaded = state.files.filter(function (f) { return f.url && !f.uploading; });
      if (uploaded.length) {
        const names = uploaded.map(function (f) { return f.name; }).join(', ');
        let filesBlock = 'Files:        ' + names + '\n';
        filesBlock += 'File URLs:\n';
        uploaded.forEach(function (f) { filesBlock += '  ' + f.url + '\n'; });

        const marker = '--- Quote request details ---';
        const idx = body.indexOf(marker);
        if (idx >= 0) {
          const insertAt = body.indexOf('\n', idx) + 1;
          body = body.slice(0, insertAt) + filesBlock + body.slice(insertAt);
        } else {
          body = filesBlock + body;
        }
      }
      messageEl.value = body;
    }

    function showError(msg) {
      let err = root.querySelector('.quote-form__file-error');
      if (!err) {
        err = document.createElement('p');
        err.className = 'quote-form__file-error';
        root.appendChild(err);
      }
      err.textContent = msg;
      setTimeout(function () { if (err) err.textContent = ''; }, 4000);
    }

    async function handleFiles(fileList) {
      const incoming = Array.from(fileList || []);
      for (const file of incoming) {
        if (state.files.length >= MAX_FILES) {
          showError('Maximum ' + MAX_FILES + ' files allowed.');
          break;
        }
        if (file.size > MAX_FILE_SIZE) {
          showError(file.name + ' is larger than 10 MB.');
          continue;
        }
        if (!ALLOWED_EXT.test(file.name)) {
          showError(file.name + ' — unsupported format.');
          continue;
        }

        // Push entry with uploading state → user sees it immediately with progress
        const entry = {
          name: file.name,
          size: file.size,
          type: file.type,
          uploading: true,
          progress: 0,
          url: null,
          fromProduct: false,
        };
        state.files.push(entry);
        renderList();

        // Upload to Cloudinary (async, don't await — let multiple files upload in parallel)
        uploadToCloudinary(file, function (pct) {
          entry.progress = pct;
          renderList();
        }).then(function (url) {
          entry.uploading = false;
          entry.url = url;
          renderList();
        }).catch(function (err) {
          console.error('[quote] Upload failed:', file.name, err);
          entry.uploading = false;
          entry.error = true;
          entry.url = null;
          renderList();
          showError('Failed: ' + file.name + ' — ' + (err.message || 'unknown error'));
        });
      }
      // Reset input so same file can be re-picked
      inputEl.value = '';
    }

    listEl.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-remove-index]');
      if (!btn) return;
      const idx = parseInt(btn.dataset.removeIndex, 10);
      if (!isNaN(idx)) {
        state.files.splice(idx, 1);
        renderList();
      }
    });

    inputEl.addEventListener('change', function (e) {
      handleFiles(e.target.files);
    });

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        inputEl.click();
      });
    }

    renderList();

    // Expose state for future backend integration (Stage 2/3)
    window.__quoteFiles = state;
  }

  function initQuotePage() {
    const summaryEl = document.querySelector('[data-quote-summary]');
    const messageEl = document.querySelector('#ContactForm-body, textarea[name*="contact[body"] i, textarea[id="ContactForm-body"]');
    const params = Object.fromEntries(new URLSearchParams(location.search).entries());

    if (!Object.keys(params).length) {
      if (summaryEl) summaryEl.style.display = 'none';
      initFileManager();
      return;
    }

    // Render visible summary block
    if (summaryEl) {
      const rows = [];
      if (params.product) rows.push(['Product', params.product]);
      if (params.tier) rows.push(['Volume tier', params.tier]);
      if (params.color) rows.push(['Color', params.color]);
      if (params.method) rows.push(['Decoration', params.method]);
      if (params.placements) rows.push(['Placement', params.placements]);
      if (params.sizes) {
        rows.push(['Sizes & Qty', params.sizes + (params.quantity ? '  (total: ' + params.quantity + ')' : '')]);
      } else if (params.quantity) {
        rows.push(['Quantity', params.quantity]);
      }
      if (params.cog_item) rows.push(['Item', params.cog_item]);
      if (params.cog_placement) rows.push(['Placement', params.cog_placement]);
      if (params.comments) rows.push(['Comments', params.comments]);
      summaryEl.innerHTML =
        '<div class="quote-summary__title">Your selection</div>' +
        '<dl class="quote-summary__list">' +
        rows
          .map(function (r) {
            return (
              '<dt>' + r[0] + '</dt><dd>' + String(r[1]).replace(/</g, '&lt;') + '</dd>'
            );
          })
          .join('') +
        '</dl>';
      summaryEl.style.display = '';
    }

    // Prefill message textarea (without file line — file manager handles that)
    const paramsForMsg = Object.assign({}, params);
    delete paramsForMsg.file;
    if (messageEl && !messageEl.value) {
      messageEl.value = formatMessageBody(paramsForMsg);
    }

    initFileManager();
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  Boot
  // ─────────────────────────────────────────────────────────────────────────
  function init() {
    if (location.pathname === '/pages/quote' || location.pathname.endsWith('/pages/quote')) {
      initQuotePage();
    }
    initProductPage();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
